import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTranslation } from '@tyro/i18n';
import { LightBulbIcon } from '@tyro/icons';
import { useNavigate } from '@tyro/router/hooks';
import { useCallback } from 'react';
import { useReaderPage } from '../../hooks/use-reader-page';
import { useReaderActions } from '../../store/documentation-reader-provider';
import { ReaderBrokenLinkAlert } from './reader-broken-link-alert';
import { ReaderProseBody } from './reader-prose-body';
import { ReaderSeeAlsoSection } from './reader-see-also-section';
import { ReaderSubPagesSection } from './reader-sub-pages-section';
import { ReaderVideoEmbed } from './reader-video-embed';

export function ReaderContent() {
  const { t } = useTranslation(['documentation']);
  const navigate = useNavigate();
  const { close, navigate: navigateReader } = useReaderActions();

  const {
    page,
    metadata,
    isLoading,
    isVideo,
    quality,
    isDocsAdmin,
    bodyMarkdown,
    editor,
    spaceSlug,
    pageSlug,
  } = useReaderPage();

  const handleSelectRelatedDoc = useCallback(
    (spaceId: number, pageSlug: string) =>
      navigateReader({ view: 'page', spaceId, pageSlug }),
    [navigateReader],
  );

  const handleOpenInEditor = useCallback(
    (targetSpaceSlug: string, targetPageSlug: string) => {
      close();
      navigate(`/documentation/edit/${targetSpaceSlug}/${targetPageSlug}`);
    },
    [close, navigate],
  );

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
        <CircularProgress size={28} />
      </Stack>
    );
  }

  if (!page) {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
        <Typography variant="body2" color="text.secondary">
          {t('documentation:reader.pageNotFound')}
        </Typography>
      </Stack>
    );
  }

  const hasBrokenOrPendingLinks =
    isDocsAdmin &&
    ((page.brokenLinkCount ?? 0) > 0 || (page.pendingLinkCount ?? 0) > 0);

  return (
    <Box sx={{ px: 3, pt: 2.5, pb: 3 }}>
      {isVideo && (
        <Typography variant="h4" sx={{ mb: 2.5 }}>
          {page.title}
        </Typography>
      )}

      {!isVideo && quality === 'stale' && (
        <Alert severity="warning" variant="outlined" sx={{ mb: 2 }}>
          {t('documentation:reader.staleWarning')}
        </Alert>
      )}
      {!isVideo && quality === 'draft' && (
        <Alert
          severity="info"
          variant="outlined"
          icon={<LightBulbIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
        >
          {t('documentation:reader.draftNotice')}
        </Alert>
      )}

      {isVideo && (
        <ReaderVideoEmbed
          videoData={page.videoData ?? null}
          title={page.title}
        />
      )}

      {hasBrokenOrPendingLinks && (
        <ReaderBrokenLinkAlert
          broken={page.brokenLinkCount ?? 0}
          pending={page.pendingLinkCount ?? 0}
          onOpenInEditor={() => handleOpenInEditor(spaceSlug, pageSlug)}
        />
      )}

      {bodyMarkdown && (
        <ReaderProseBody
          editor={editor}
          hasVideoAbove={isVideo}
          spaceSlug={spaceSlug}
          pageSlug={pageSlug}
          onOpenInEditor={handleOpenInEditor}
        />
      )}

      <ReaderSubPagesSection pageId={page.id} spaceId={page.spaceId} />

      <ReaderSeeAlsoSection
        metadata={metadata}
        onSelectRelatedDoc={handleSelectRelatedDoc}
      />
    </Box>
  );
}
